<template>
  <div class="tech-center-container">
    <!-- 视差滚动顶部横幅 -->
    <div class="hero-section" ref="heroSection">
      <div class="hero-content" :style="{ transform: `translateY(${scrollY * 0.5}px)` }">
        <h1 class="animate__animated animate__fadeInUp">技术中心</h1>
        <p class="animate__animated animate__fadeInUp animate__delay-1s">
          先进的光学技术解决方案
        </p>
      </div>
      <div class="hero-overlay"></div>
    </div>

    <!-- 技术概览 -->
    <div class="tech-overview">
      <div class="tech-nav">
        <div class="nav-container">
          <div 
            v-for="(tech, index) in techList" 
            :key="index"
            class="nav-item"
            :class="{ active: currentTechIndex === index }"
            @click="currentTechIndex = index"
          >
            {{ tech.title }}
          </div>
        </div>
      </div>
      <div class="overview-content">
        <div class="overview-text" v-scrollanimation>
          <h2>{{ currentTech.title }}</h2>
          <p>{{ currentTech.description }}</p>
        </div>
        <div class="overview-image" v-scrollanimation>
          <el-image :src="currentTech.image" fit="cover">
            <template #placeholder>
              <div class="image-placeholder">
                <el-icon><Picture /></el-icon>
              </div>
            </template>
          </el-image>
        </div>
      </div>
    </div>

    <!-- 技术特点 -->
    <div class="features-section">
      <div class="section-title" v-scrollanimation>
        <h2>技术特点</h2>
        <div class="title-line"></div>
      </div>
      <div class="features-grid">
        <div 
          v-for="(feature, index) in currentTech.features" 
          :key="index"
          class="feature-card"
          v-scrollanimation
          :style="{ 'animation-delay': `${index * 0.2}s` }"
        >
          <div class="feature-icon">
            <el-icon><Check /></el-icon>
          </div>
          <h3>{{ feature }}</h3>
        </div>
      </div>
    </div>

    <!-- 技术参数 -->
    <div class="parameters-section">
      <div class="parameters-content">
        <div class="parameters-table" v-scrollanimation>
          <h2>技术参数</h2>
          <el-table :data="parametersData" :show-header="false" style="width: 100%">
            <el-table-column prop="key" width="200">
              <template #default="scope">
                <span class="param-key">{{ scope.row.key }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="value">
              <template #default="scope">
                <span class="param-value">{{ scope.row.value }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="parameters-image" v-scrollanimation>
          <el-image :src="currentTech.image" fit="contain" />
        </div>
      </div>
    </div>

    <!-- 技术原理 -->
    <div class="principle-section">
      <div class="principle-content">
        <div class="principle-text" v-scrollanimation>
          <h2>技术原理</h2>
          <div class="principle-formula">
            <p>临界角计算公式：</p>
            <div class="formula">{{ currentTech.principle.formulas[0].formula }}</div>
            <p>全宽半高：</p>
            <div class="formula">{{ currentTech.principle.formulas[1].formula }}</div>
          </div>
          <div class="principle-description" v-html="formattedDescription"></div>
        </div>
        <div class="principle-diagram" v-scrollanimation>
          <el-image :src="currentTech.principle.image" fit="contain" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Picture, Check } from '@element-plus/icons-vue'

export default {
  name: 'TechnologyCenter',
  components: {
    Picture,
    Check
  },
  directives: {
    scrollanimation: {
      mounted: (el) => {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              el.classList.add('animate-in')
              observer.unobserve(el)
            }
          },
          {
            threshold: 0.1
          }
        )
        observer.observe(el)
      }
    }
  },
  setup() {
    const scrollY = ref(0)
    const heroSection = ref(null)
    const currentTechIndex = ref(0)

    // 技术列表数据
    const techList = [
      {
        id: 'xray-optics',
        title: '多毛细管光学器件对X射线束整形技术',
        description: '多毛细管光学元件由数百根极细的玻璃毛细管组成，这些毛细管被拉成精确定义的形状。多毛细管光学元件可用于 X 射线传输、成型和聚焦。我们可根据具体应用需求，协助开展光学元件选型与技术方案评估。',
        image: '/images/原理功能.png',
        features: [
          'X 射线传输与成型',
          '聚焦与准直应用',
          '按需求协助选型',
          '灵活的几何参数'
        ],
        parameters: {
          'X射线源特性': '可根据需求定制',
          '光束形状': '可定制',
          '焦斑大小': '可定制',
          '光子能量范围': '可根据应用调整',
          '强度增益': '500-1000倍（相比针孔准直器）'
        },
        principle: {
          title: 'X射线毛细管光学系统工作原理',
          description: `X 射线毛细管光学系统的工作模式是基于 X 射线在非常光滑的玻璃毛细管内表面上的外部全反射效应。对于极小的入射角，几乎 100% 的 X 射线都会被毛细管的玻璃壁反射，因此可以极低的损耗引导 X 射线通过光学器件。

通过对毛细管束进行选择性变形，可以制造出具有入射角、焦距和焦点大小等精确定义特性的光学器件。它们可以将 X 射线集中在样品表面的特定区域。与相同直径的针孔准直器相比，可实现高五百到一千倍的强度。

指定光学器件的基本信息包括 X 射线源的特性、所需的光束形状、所需的焦斑大小以及需要有效引导的光子能量范围。由于几何参数的高度灵活性，聚毛细管光学器件可用于各种不同的应用领域。`,
          image: '/images/原理功能.png',
          formulas: [
            {
              name: '临界角计算公式',
              formula: 'θcr [rad] ≈ 0.02·√(ρ [g·cm⁻³]/E [keV])'
            },
            {
              name: '全宽半高',
              formula: 'FWHM ≈ F·θcr'
            }
          ]
        }
      }
      // 这里可以添加更多技术
    ]

    const currentTech = computed(() => techList[currentTechIndex.value])

    const parametersData = computed(() => {
      const params = currentTech.value.parameters
      return Object.entries(params).map(([key, value]) => ({
        key,
        value
      }))
    })

    const formattedDescription = computed(() => {
      return currentTech.value.principle.description.split('\n').map(p => `<p>${p}</p>`).join('')
    })

    const handleScroll = () => {
      scrollY.value = window.scrollY
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    return {
      scrollY,
      heroSection,
      currentTechIndex,
      techList,
      currentTech,
      parametersData,
      formattedDescription
    }
  }
}
</script>

<style scoped>
.tech-center-container {
  width: 100%;
}

/* 视差滚动顶部横幅 */
.hero-section {
  height: 600px;
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  text-align: center;
  color: white;
  z-index: 2;
  padding: 0 20px;
}

.hero-content h1 {
  font-size: 56px;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 2px;
}

.hero-content p {
  font-size: 24px;
  opacity: 0.9;
  letter-spacing: 1px;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1;
}

/* 技术概览 */
.tech-overview {
  padding: 100px 0;
  background: white;
}

.overview-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.overview-text h2 {
  font-size: 36px;
  color: #2c3e50;
  margin-bottom: 30px;
}

.overview-text p {
  font-size: 18px;
  line-height: 1.8;
  color: #4a5568;
}

.overview-image {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 技术特点 */
.features-section {
  padding: 100px 0;
  background: #f8fafc;
}

.section-title {
  text-align: center;
  margin-bottom: 60px;
}

.section-title h2 {
  font-size: 36px;
  color: #2c3e50;
  margin-bottom: 20px;
}

.title-line {
  width: 60px;
  height: 3px;
  background: #1890ff;
  margin: 0 auto;
}

.features-grid {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.feature-card {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
}

.feature-icon {
  font-size: 32px;
  color: #1890ff;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 20px;
  color: #2c3e50;
}

/* 技术参数 */
.parameters-section {
  padding: 100px 0;
  background: white;
}

.parameters-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.parameters-table h2 {
  font-size: 36px;
  color: #2c3e50;
  margin-bottom: 40px;
}

.param-key {
  font-weight: 600;
  color: #2c3e50;
}

.param-value {
  color: #4a5568;
}

.parameters-image {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 技术原理 */
.principle-section {
  padding: 100px 0;
  background: #f8fafc;
}

.principle-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
}

.principle-text h2 {
  font-size: 36px;
  color: #2c3e50;
  margin-bottom: 40px;
}

.principle-formula {
  margin-bottom: 40px;
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.principle-formula p {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 10px;
}

.formula {
  font-family: 'Times New Roman', serif;
  font-size: 24px;
  color: #1890ff;
  margin: 20px 0;
  padding: 10px;
  background: #f0f9ff;
  border-radius: 4px;
  text-align: center;
}

.principle-description {
  font-size: 18px;
  line-height: 1.8;
  color: #4a5568;
}

.principle-description p {
  margin-bottom: 20px;
}

.principle-diagram {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 滚动动画 */
[v-scrollanimation] {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.6s ease;
}

[v-scrollanimation].animate-in {
  opacity: 1;
  transform: translateY(0);
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .overview-content,
  .parameters-content,
  .principle-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .overview-image,
  .parameters-image,
  .principle-diagram {
    max-width: 600px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 400px;
  }

  .hero-content h1 {
    font-size: 36px;
  }

  .hero-content p {
    font-size: 18px;
  }

  .overview-content,
  .parameters-content,
  .principle-content {
    padding: 0 20px;
  }

  .features-grid {
    padding: 0 20px;
    gap: 20px;
  }

  .feature-card {
    padding: 30px;
  }

  .principle-formula {
    padding: 20px;
  }

  .formula {
    font-size: 20px;
  }
}

/* 技术导航样式 */
.tech-nav {
  background: white;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 60px;
  z-index: 100;
  margin-bottom: 60px;
}

.nav-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  gap: 40px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.nav-container::-webkit-scrollbar {
  display: none;
}

.nav-item {
  padding: 20px 0;
  font-size: 18px;
  color: #4a5568;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  transition: all 0.3s ease;
}

.nav-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: #1890ff;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.nav-item:hover {
  color: #1890ff;
}

.nav-item.active {
  color: #1890ff;
}

.nav-item.active::after {
  transform: scaleX(1);
}
</style> 